import { useSignal } from '@preact/signals';
import type { JourneyPlan } from '../arcticapi/types';
import {
  currentLocationToPlaceSearchResult,
  type PositionProvider,
} from './currentLocation';
import type { PlaceOption } from './places';
import { useCurrentLocation } from './useCurrentLocation';
import { usePlaceSearch } from './usePlaceSearch';
import { useJourneyPlan } from './useJourneyPlan';
import {
  buildJourneyPlanApiRequest,
  createDefaultJourneyPlanAdvancedFilters,
  createDefaultJourneyPlanTimeSelection,
  type JourneyPlanAdvancedFilters,
  type JourneyPlanLocation,
  type JourneyPlanSearchRequest,
  type JourneyPlanTimeSelection,
  type JourneyPlanTimeMode,
} from './journeyPlanSearch';

/**
 * UI state and actions for the full journey-planning form interaction.
 */
export interface JourneyPlanningFormLocationsState {
  originQuery: string;
  destinationQuery: string;
  originOptions: PlaceOption[];
  destinationOptions: PlaceOption[];
  onOriginChange: (value: string) => void;
  onDestinationChange: (value: string) => void;
  onOriginSelectOption: (option: PlaceOption) => void;
  onDestinationSelectOption: (option: PlaceOption) => void;
  onSwapLocations: () => void;
  handleUseCurrentOrigin: () => Promise<void>;
  handleUseCurrentDestination: () => Promise<void>;
  originIsCurrentLocation: boolean;
  destinationIsCurrentLocation: boolean;
  originPlacesLoading: boolean;
  destinationPlacesLoading: boolean;
  currentLocationLoading: boolean;
  error: Error | null;
}

export interface JourneyPlanningFormTimeState {
  mode: JourneyPlanTimeMode;
  date: string;
  time: string;
  onModeChange: (mode: JourneyPlanTimeMode) => void;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
}

export interface JourneyPlanningFormFiltersState {
  value: JourneyPlanAdvancedFilters;
  onChange: (filters: JourneyPlanAdvancedFilters) => void;
}

export interface JourneyPlanningFormSubmitState {
  submitJourneySearch: () => boolean;
  handleSubmit: (event: SubmitEvent) => boolean;
  error: string | null;
}

export interface JourneyPlanningFormState {
  locations: JourneyPlanningFormLocationsState;
  time: JourneyPlanningFormTimeState;
  filters: JourneyPlanningFormFiltersState;
  submit: JourneyPlanningFormSubmitState;
}

export interface SubmittedJourneyPlanningSearch {
  locations: Pick<JourneyPlanSearchRequest, 'destination' | 'origin'>;
  time: JourneyPlanTimeSelection;
  filters: JourneyPlanAdvancedFilters;
}

export interface JourneyPlanningPaginationLink {
  title?: string;
  time?: string;
}

export interface JourneyPlanningPaginationState {
  earlier: JourneyPlanningPaginationLink | null;
  later: JourneyPlanningPaginationLink | null;
  fetchEarlier: () => boolean;
  fetchLater: () => boolean;
}

export interface JourneyPlanningResultsState {
  hasPlannedJourney: boolean;
  loading: boolean;
  error: Error | null;
  plans: JourneyPlan[];
  submittedSearch: SubmittedJourneyPlanningSearch | null;
  pagination: JourneyPlanningPaginationState;
}

export interface JourneyPlanningState {
  form: JourneyPlanningFormState;
  results: JourneyPlanningResultsState;
}

interface UseJourneyPlanningOptions {
  initialOrigin?: string;
  initialDestination?: string;
  currentLocationPositionProvider?: PositionProvider;
}

/**
 * Models the full "plan your journey" user task.
 *
 * "Journey planning" is the higher-level flow: origin + destination place
 * lookup, validation, and then submission to create a journey plan.
 */
export function useJourneyPlanning(
  options: UseJourneyPlanningOptions = {},
): JourneyPlanningState {
  const submitError = useSignal<string | null>(null);
  const timeMode = useSignal<JourneyPlanTimeMode>(
    createDefaultJourneyPlanTimeSelection().mode,
  );
  const journeyDate = useSignal(createDefaultJourneyPlanTimeSelection().date);
  const journeyTime = useSignal(createDefaultJourneyPlanTimeSelection().time);
  const advancedFilters = useSignal<JourneyPlanAdvancedFilters>(
    createDefaultJourneyPlanAdvancedFilters(),
  );
  const originIsCurrentLocation = useSignal(false);
  const destinationIsCurrentLocation = useSignal(false);
  const submittedSearch = useSignal<SubmittedJourneyPlanningSearch | null>(
    null,
  );

  const journeyPlan = useJourneyPlan();
  const currentLocation = useCurrentLocation({
    positionProvider: options.currentLocationPositionProvider,
  });
  const originSearch = usePlaceSearch(options.initialOrigin ?? '', {
    currentCoordinates: currentLocation.currentLocation,
  });
  const destinationSearch = usePlaceSearch(options.initialDestination ?? '', {
    currentCoordinates: currentLocation.currentLocation,
  });

  const handleUseCurrentOrigin = async (): Promise<void> => {
    submitError.value = null;
    currentLocation.clearError();

    try {
      const location = await currentLocation.resolveCurrentLocation();
      originSearch.setResolvedPlace(
        currentLocationToPlaceSearchResult(location),
        location.label,
      );
      originIsCurrentLocation.value = true;
    } catch {
      // Error state is tracked by useCurrentLocation and surfaced below.
    }
  };
  const handleUseCurrentDestination = async (): Promise<void> => {
    submitError.value = null;
    currentLocation.clearError();

    try {
      const location = await currentLocation.resolveCurrentLocation();
      destinationSearch.setResolvedPlace(
        currentLocationToPlaceSearchResult(location),
        location.label,
      );
      destinationIsCurrentLocation.value = true;
    } catch {
      // Error state is tracked by useCurrentLocation and surfaced below.
    }
  };

  const handleSelectPlaceOption = (
    search: typeof originSearch,
    option: PlaceOption,
  ) => {
    submitError.value = null;

    if (!option.place) {
      return;
    }

    search.setResolvedPlace(option.place, option.value);
  };

  const handleSwapLocations = () => {
    submitError.value = null;

    const originPlace = originSearch.resolveSelectedPlace();
    const destinationPlace = destinationSearch.resolveSelectedPlace();
    const originText = originSearch.query;
    const destinationText = destinationSearch.query;
    const originWasCurrentLocation = originIsCurrentLocation.value;
    const destinationWasCurrentLocation = destinationIsCurrentLocation.value;

    originSearch.setResolvedPlace(destinationPlace, destinationText);
    destinationSearch.setResolvedPlace(originPlace, originText);

    originIsCurrentLocation.value = destinationWasCurrentLocation;
    destinationIsCurrentLocation.value = originWasCurrentLocation;
  };

  const submitJourneySearch = (): boolean => {
    submitError.value = null;

    const originPlace = originSearch.resolveSelectedPlace();
    const destinationPlace = destinationSearch.resolveSelectedPlace();

    if (!originPlace && !destinationPlace) {
      submitError.value = 'Enter a start point and a destination.';
      return false;
    }

    if (!originPlace) {
      submitError.value = 'Enter a starting point.';
      return false;
    }

    if (!destinationPlace) {
      submitError.value = 'Enter a destination.';
      return false;
    }

    originSearch.setResolvedPlace(originPlace);
    destinationSearch.setResolvedPlace(destinationPlace);

    const searchRequest: JourneyPlanSearchRequest = {
      origin: toJourneyPlanLocation(
        originPlace.location.coordinates,
        originPlace.commonName,
      ),
      destination: toJourneyPlanLocation(
        destinationPlace.location.coordinates,
        destinationPlace.commonName,
      ),
      time: {
        mode: timeMode.value,
        date: journeyDate.value,
        time: journeyTime.value,
      },
      filters: advancedFilters.value,
    };

    submittedSearch.value = {
      locations: {
        origin: searchRequest.origin,
        destination: searchRequest.destination,
      },
      time: searchRequest.time,
      filters: searchRequest.filters,
    };

    journeyPlan.submitJourneyPlan(buildJourneyPlanApiRequest(searchRequest));

    return true;
  };

  const handleSubmit = (event: SubmitEvent): boolean => {
    event.preventDefault();

    return submitJourneySearch();
  };

  return {
    form: {
      locations: {
        originQuery: originSearch.query,
        destinationQuery: destinationSearch.query,
        originOptions: originSearch.options,
        destinationOptions: destinationSearch.options,
        onOriginChange: (value: string) => {
          originSearch.setQuery(value);
          originIsCurrentLocation.value = false;
        },
        onDestinationChange: (value: string) => {
          destinationSearch.setQuery(value);
          destinationIsCurrentLocation.value = false;
        },
        onOriginSelectOption: (option) => {
          handleSelectPlaceOption(originSearch, option);
          originIsCurrentLocation.value = false;
        },
        onDestinationSelectOption: (option) => {
          handleSelectPlaceOption(destinationSearch, option);
          destinationIsCurrentLocation.value = false;
        },
        onSwapLocations: handleSwapLocations,
        handleUseCurrentOrigin,
        handleUseCurrentDestination,
        originIsCurrentLocation: originIsCurrentLocation.value,
        destinationIsCurrentLocation: destinationIsCurrentLocation.value,
        originPlacesLoading: originSearch.loading,
        destinationPlacesLoading: destinationSearch.loading,
        currentLocationLoading: currentLocation.locating,
        error:
          originSearch.error ??
          destinationSearch.error ??
          currentLocation.error,
      },
      time: {
        mode: timeMode.value,
        date: journeyDate.value,
        time: journeyTime.value,
        onModeChange: (mode) => {
          timeMode.value = mode;
        },
        onDateChange: (value) => {
          journeyDate.value = value;
        },
        onTimeChange: (value) => {
          journeyTime.value = value;
        },
      },
      filters: {
        value: advancedFilters.value,
        onChange: (filters) => {
          advancedFilters.value = filters;
        },
      },
      submit: {
        submitJourneySearch,
        handleSubmit,
        error: submitError.value,
      },
    },
    results: {
      hasPlannedJourney: journeyPlan.hasSubmitted,
      loading: journeyPlan.loading,
      error: journeyPlan.error,
      plans: journeyPlan.plans,
      submittedSearch: submittedSearch.value,
      pagination: {
        earlier: journeyPlan.earlierPage,
        later: journeyPlan.laterPage,
        fetchEarlier: journeyPlan.fetchEarlier,
        fetchLater: journeyPlan.fetchLater,
      },
    },
  };
}

function toJourneyPlanLocation(
  coordinate: [number, number],
  name: string,
): JourneyPlanLocation {
  return {
    coordinate,
    name,
  };
}
